/**
 * The one unresolved thing a reminder plan can leave behind: a create whose outcome is UNKNOWN.
 *
 * A plan is created (and rescheduled) by ONE atomic call that writes every step or none, so there is no
 * half-made plan to finish, cancel or reconstruct. What can still happen is a timeout or a 5xx, where the
 * client does not know whether the call landed. The host must then never mint a NEW plan id for the same
 * intent (that would create a second plan if the first landed). So the attempt (its id and exact steps) is
 * remembered per trip in sessionStorage until it resolves — its rows show up on the scheduled list, the same
 * call is retried (idempotent by id) or the host discards it after the list shows nothing.
 */
import type { PlanRequest } from './reminderPlan';

// ---- attempts this tab made whose outcome is unknown ----
//
// Kept in sessionStorage (this tab, surviving a refresh). Another tab has its own; a plan id is never shared
// between tabs, so two tabs creating a plan make two plans (several plans per trip are allowed).

export interface PlanAttempt {
  sequenceId: string;
  /** The trip start date the plan is built from (sent as its anchor on every retry). */
  anchorDate: string;
  requests: PlanRequest[];
  /** When the call for this plan was last SENT (ms). A request the client gave up on can still be committing for a while after this. */
  lastSentAt: number;
  /**
   * A call of this plan had an UNKNOWN outcome (a timeout or 5xx that may have committed). From then on a retry's 400 or 503 does not settle it
   * (the earlier request may still be committing): only `/abandon` or the plan's rows appearing on the list do.
   */
  unknownOutcome?: boolean;
  /** Why the attempt is still open, in the host's words (shown beside Retry / Discard, so a disabled Create is never unexplained). */
  note?: string;
}

const STORAGE_PREFIX = 'studio.reminderPlan.attempt.';
const EMPTY: readonly PlanAttempt[] = Object.freeze([]);
const attempts = new Map<string, readonly PlanAttempt[]>();
const listeners = new Set<() => void>();
let loaded = false;
const notify = () => listeners.forEach((listener) => listener());

function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function persist(groupId: string, attempt: PlanAttempt | null, sequenceId: string) {
  const store = storage();
  if (!store) return;
  try {
    if (attempt) store.setItem(STORAGE_PREFIX + sequenceId, JSON.stringify({ groupId, ...attempt }));
    else store.removeItem(STORAGE_PREFIX + sequenceId);
  } catch {
    // Storage full or blocked: the in-memory copy still serves this page load.
  }
}

/** Read what an earlier page load of this tab remembered. Runs once. */
function load() {
  if (loaded) return;
  loaded = true;
  const store = storage();
  if (!store) return;
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      const parsed = JSON.parse(store.getItem(key) ?? 'null') as (PlanAttempt & { groupId?: string }) | null;
      if (!parsed?.groupId || !parsed.sequenceId || !parsed.anchorDate || !Array.isArray(parsed.requests)) continue;
      const { groupId, ...attempt } = parsed;
      attempts.set(groupId, [...(attempts.get(groupId) ?? EMPTY), attempt]);
    }
  } catch {
    // A corrupt entry is ignored: worst case an unresolved attempt is forgotten on reload, and any plan that did land is on the list.
  }
}

/** Remember an attempt that may not have finished. Replaces an earlier one for the same plan. */
export function markUnresolvedPlan(groupId: string, attempt: PlanAttempt): void {
  load();
  const earlier = (attempts.get(groupId) ?? EMPTY).find((entry) => entry.sequenceId === attempt.sequenceId);
  const others = (attempts.get(groupId) ?? EMPTY).filter((entry) => entry.sequenceId !== attempt.sequenceId);
  // A re-send of the same plan must not forget that an earlier call of it was unknown.
  const merged: PlanAttempt = { ...attempt, unknownOutcome: attempt.unknownOutcome ?? earlier?.unknownOutcome };
  attempts.set(groupId, [...others, merged]);
  persist(groupId, merged, merged.sequenceId);
  notify();
}

/** Update a remembered attempt in place (its unknown-outcome flag, or the note explaining why it is open). No-op when it is not remembered. */
export function updateUnresolvedPlan(groupId: string, sequenceId: string, patch: Partial<Pick<PlanAttempt, 'unknownOutcome' | 'note'>>): void {
  load();
  const current = attempts.get(groupId) ?? EMPTY;
  const earlier = current.find((entry) => entry.sequenceId === sequenceId);
  if (!earlier) return;
  const next: PlanAttempt = { ...earlier, ...patch };
  attempts.set(groupId, current.map((entry) => (entry === earlier ? next : entry)));
  persist(groupId, next, sequenceId);
  notify();
}

/** Forget one plan's attempt — because it was cancelled, finished, or the server confirmed it never existed. */
export function clearUnresolvedPlan(groupId: string, sequenceId?: string | null): void {
  load();
  const current = attempts.get(groupId);
  if (!current) return;
  const next = sequenceId ? current.filter((entry) => entry.sequenceId !== sequenceId) : [];
  if (next.length === current.length) return;
  current.filter((entry) => !next.includes(entry)).forEach((entry) => persist(groupId, null, entry.sequenceId));
  if (next.length === 0) attempts.delete(groupId);
  else attempts.set(groupId, next);
  notify();
}

/** Stable between changes, so it can be a `useSyncExternalStore` snapshot. */
export function unresolvedPlans(groupId: string): readonly PlanAttempt[] {
  load();
  return attempts.get(groupId) ?? EMPTY;
}

export function unresolvedPlan(groupId: string, sequenceId: string): PlanAttempt | null {
  return unresolvedPlans(groupId).find((entry) => entry.sequenceId === sequenceId) ?? null;
}

/** `useSyncExternalStore` subscription. */
export function subscribeUnresolvedPlans(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Test seam: drop every remembered attempt (memory and sessionStorage). `reload` also forgets what was loaded, to model a page refresh. */
export function resetUnresolvedPlans(options: { keepStorage?: boolean } = {}): void {
  attempts.clear();
  loaded = false;
  if (!options.keepStorage) {
    const store = storage();
    try {
      if (store) [...Array(store.length).keys()].map((i) => store.key(i)).forEach((key) => { if (key?.startsWith(STORAGE_PREFIX)) store.removeItem(key); });
    } catch {
      // ignore
    }
  }
  notify();
}
